import React, { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { resolveMediaUrl } from "@/lib/api";
import { openWhatsApp } from "@/lib/whatsapp";
import { Calendar } from "@/components/ui/calendar";
import { useEmployer } from "@/context/EmployerContext";
import { useBasket } from "@/context/BasketContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ShieldCheck, MailCheck, Loader2, Plus, X, UserPlus, Search } from "lucide-react";

const MAX_CANDIDATES = 3;

// Common IANA timezones covering Adelina's employer base (Brunei, SG, MY, HK, TW, ME).
// Browser's current timezone is auto-appended if not in the list.
const TIMEZONE_OPTIONS = (() => {
  const base = [
    "Asia/Brunei",
    "Asia/Singapore",
    "Asia/Kuala_Lumpur",
    "Asia/Jakarta",
    "Asia/Hong_Kong",
    "Asia/Taipei",
    "Asia/Dubai",
    "Asia/Riyadh",
    "Asia/Manila",
    "Asia/Tokyo",
    "Australia/Sydney",
    "Europe/London",
    "America/New_York",
    "America/Los_Angeles",
    "UTC",
  ];
  try {
    const local = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (local && !base.includes(local)) return [local, ...base];
  } catch { /* ignore — browsers without Intl support */ }
  return base;
})();

/**
 * Per-candidate schedule row. Owns its own date/time state via `schedule`
 * prop lifted to the parent so submission can gather all items at once.
 */
function CandidateScheduleCard({ index, candidate, schedule, onSchedule, onRemove, t }) {
  const { date, time } = schedule || {};
  const dateStr = date ? date.toISOString().slice(0, 10) : "";
  const { data: slots } = useQuery({
    queryKey: ["slots", candidate.id, dateStr],
    queryFn: async () => (await api.get("/bookings/slots", { params: { candidate_id: candidate.id, date: dateStr } })).data,
    enabled: !!dateStr && !!candidate.id,
  });
  const unavailable = new Set(slots?.unavailable || []);
  const morning = slots?.morning || [];
  const afternoon = slots?.afternoon || [];

  const setDate = (d) => onSchedule(candidate.id, { date: d, time: "" });
  const setTime = (slot) => onSchedule(candidate.id, { date, time: slot });

  return (
    <div className="border border-zinc-200 rounded-2xl p-5 bg-white" data-testid={`booking-candidate-card-${index}`}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          {candidate.photo_url ? (
            <img src={resolveMediaUrl(candidate.photo_url)} alt={candidate.name} className="w-12 h-12 rounded-xl object-cover" />
          ) : (
            <div className="w-12 h-12 rounded-xl bg-zinc-100 border border-zinc-200 flex items-center justify-center text-zinc-400 font-serif text-lg" aria-label={candidate.name}>
              {(candidate.name || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
              {t("booking.candidate_n", "Candidate #{{n}}", { n: index + 1 })}
            </div>
            <div className="font-serif text-lg text-zinc-950 truncate">{candidate.name}</div>
            <div className="text-xs text-zinc-600 truncate">{candidate.role} · {candidate.nationality || ""}</div>
          </div>
        </div>
        {onRemove && (
          <button type="button" onClick={onRemove} className="shrink-0 text-xs text-red-700 hover:text-red-900 inline-flex items-center gap-1" data-testid={`booking-remove-candidate-${index}`}>
            <X size={14} /> {t("booking.remove", "Remove")}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-600 mb-2 font-semibold">
            {t("booking.step_date", "Date")}
          </div>
          <div className="rounded-2xl border border-zinc-200 p-2 inline-block bg-zinc-50/40">
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={(d) => d < new Date(new Date().toDateString())}
              data-testid={`booking-calendar-${index}`}
            />
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-600 mb-2 font-semibold">
            {t("booking.step_time", "Time slot")}
          </div>
          {!dateStr ? (
            <div className="text-sm text-zinc-500 italic">{t("booking.pick_date_first", "Pick a date first")}</div>
          ) : (
            <>
              <div className="mb-3">
                <div className="text-[10px] text-zinc-500 mb-1.5 uppercase tracking-wider">{t("booking.morning", "Morning")}</div>
                <div className="flex flex-wrap gap-1.5">
                  {morning.map((slot) => {
                    const u = unavailable.has(slot);
                    return (
                      <button
                        type="button"
                        key={slot}
                        disabled={u}
                        onClick={() => setTime(slot)}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                          time === slot ? "bg-[#b8860b] border-[#b8860b] text-zinc-950 font-semibold" :
                          u ? "bg-zinc-100 border-zinc-200 text-zinc-400 line-through cursor-not-allowed" :
                          "border-zinc-300 hover:border-[#b8860b] text-zinc-800"
                        }`}
                        data-testid={`slot-${index}-${slot}`}
                      >
                        {slot}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-zinc-500 mb-1.5 uppercase tracking-wider">{t("booking.afternoon", "Afternoon")}</div>
                <div className="flex flex-wrap gap-1.5">
                  {afternoon.map((slot) => {
                    const u = unavailable.has(slot);
                    return (
                      <button
                        type="button"
                        key={slot}
                        disabled={u}
                        onClick={() => setTime(slot)}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                          time === slot ? "bg-[#b8860b] border-[#b8860b] text-zinc-950 font-semibold" :
                          u ? "bg-zinc-100 border-zinc-200 text-zinc-400 line-through cursor-not-allowed" :
                          "border-zinc-300 hover:border-[#b8860b] text-zinc-800"
                        }`}
                        data-testid={`slot-${index}-${slot}`}
                      >
                        {slot}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Inline candidate picker — searchable list, filters out already-picked ids. */
function CandidatePicker({ excludeIds, onPick, onCancel, t }) {
  const [q, setQ] = useState("");
  const { data: candidates = [], isLoading } = useQuery({
    queryKey: ["catalog-candidates"],
    queryFn: async () => (await api.get("/candidates", { params: { limit: 500 } })).data,
    staleTime: 60_000,
  });
  const filtered = useMemo(() => {
    const norm = (s) => (s || "").toLowerCase();
    const term = norm(q).trim();
    return (candidates || [])
      .filter((c) => !excludeIds.includes(c.id))
      .filter((c) => c.status !== "Selected" && c.status !== "Departed" && c.status !== "Inactive")
      .filter((c) => !term
        || norm(c.name).includes(term)
        || norm(c.role).includes(term)
        || norm(c.nationality).includes(term))
      .slice(0, 40);
  }, [candidates, q, excludeIds]);

  return (
    <div className="border-2 border-dashed border-[#b8860b]/40 rounded-2xl p-5 bg-amber-50/30" data-testid="booking-candidate-picker">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <UserPlus size={16} className="text-[#b8860b]" />
          <div className="text-sm font-semibold text-zinc-950">
            {t("booking.pick_another", "Pick another candidate")}
          </div>
        </div>
        <button type="button" onClick={onCancel} className="text-xs text-zinc-600 hover:text-zinc-950 inline-flex items-center gap-1" data-testid="booking-picker-cancel">
          <X size={12} /> {t("common.cancel", "Cancel")}
        </button>
      </div>

      <div className="relative mb-3">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("booking.search_placeholder", "Search by name, role, nationality…")}
          className="pl-9 h-10"
          data-testid="booking-picker-search"
          autoFocus
        />
      </div>

      <div className="max-h-72 overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-2">
        {isLoading && <div className="text-sm text-zinc-500 col-span-full">Loading…</div>}
        {!isLoading && filtered.length === 0 && (
          <div className="text-sm text-zinc-500 col-span-full text-center py-6">
            {t("booking.no_match", "No matching candidates.")}
          </div>
        )}
        {filtered.map((c) => (
          <button
            type="button"
            key={c.id}
            onClick={() => onPick(c)}
            className="flex items-center gap-3 p-2 pr-3 rounded-xl border border-zinc-200 bg-white hover:border-[#b8860b] hover:shadow-sm transition text-left"
            data-testid={`booking-picker-item-${c.id}`}
          >
            {c.photo_url ? (
              <img src={resolveMediaUrl(c.photo_url)} alt={c.name} className="w-10 h-10 rounded-lg object-cover shrink-0" />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-zinc-100 border border-zinc-200 flex items-center justify-center text-zinc-400 text-sm font-serif shrink-0">
                {(c.name || "?").slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-sm font-semibold text-zinc-950 truncate">{c.name}</div>
              <div className="text-xs text-zinc-600 truncate">{c.role} · {c.nationality || ""}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Booking() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const fromBasket = searchParams.get("basket") === "1";
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { employer } = useEmployer() || {};
  const basket = useBasket();

  // Primary candidate loaded from the URL; extras are added client-side.
  const { data: primaryCandidate } = useQuery({
    queryKey: ["candidate", id],
    queryFn: async () => (await api.get(`/candidates/${id}`)).data,
  });
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
  });
  const otpEnabled = Boolean(settings?.require_booking_otp);

  // Selected candidates (max 3, primary always first).
  const [extras, setExtras] = useState([]); // additional candidate objects
  const [pickerOpen, setPickerOpen] = useState(false);
  const [schedules, setSchedules] = useState({}); // { [candidateId]: { date, time } }

  // When arriving via ?basket=1 (from BasketSidebar / CandidateCard), auto-fill
  // extras with the remaining basket items that aren't the primary candidate.
  // Runs once when the primary candidate loads to avoid clobbering user edits.
  const [basketApplied, setBasketApplied] = useState(false);
  useEffect(() => {
    if (!fromBasket || basketApplied || !primaryCandidate) return;
    const rest = basket.items.filter((c) => c.id !== primaryCandidate.id).slice(0, MAX_CANDIDATES - 1);
    if (rest.length) setExtras(rest);
    setBasketApplied(true);
  }, [fromBasket, basketApplied, primaryCandidate, basket.items]);

  const candidates = useMemo(() => (primaryCandidate ? [primaryCandidate, ...extras] : []), [primaryCandidate, extras]);

  const setSchedule = (candId, patch) => {
    setSchedules((s) => ({ ...s, [candId]: { ...(s[candId] || {}), ...patch } }));
  };

  const removeExtra = (candId) => {
    setExtras((s) => s.filter((c) => c.id !== candId));
    setSchedules((s) => {
      const { [candId]: _, ...rest } = s;
      return rest;
    });
  };

  const pickCandidate = (c) => {
    if (candidates.some((x) => x.id === c.id)) {
      toast.error(t("booking.duplicate", "Candidate already in booking"));
      return;
    }
    if (candidates.length >= MAX_CANDIDATES) {
      toast.error(t("booking.max_reached", "Max {{n}} candidates", { n: MAX_CANDIDATES }));
      return;
    }
    setExtras((s) => [...s, c]);
    setPickerOpen(false);
  };

  const [form, setForm] = useState({
    employer_name: "",
    employer_phone: "",
    employer_email: "",
    company: "",
    notes: "",
    employer_timezone: (() => {
      try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Brunei"; }
      catch { return "Asia/Brunei"; }
    })(),
  });
  useEffect(() => {
    if (employer) {
      setForm((f) => ({
        ...f,
        employer_name: f.employer_name || employer.name || "",
        employer_phone: f.employer_phone || employer.phone || "",
        employer_email: f.employer_email || employer.email || "",
        company: f.company || employer.company || "",
      }));
    }
  }, [employer]);

  const [submitting, setSubmitting] = useState(false);
  // Confirmation modal — user must review the booking summary before the
  // form is actually submitted + WhatsApp handoff fires.
  const [showConfirm, setShowConfirm] = useState(false);
  // OTP anti-abuse flow
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpRequesting, setOtpRequesting] = useState(false);
  const [otpVerifiedEmail, setOtpVerifiedEmail] = useState("");

  const changeEmail = (v) => {
    setForm((p) => ({ ...p, employer_email: v }));
    if (otpSent && v.trim().toLowerCase() !== otpVerifiedEmail) {
      setOtpSent(false);
      setOtpCode("");
    }
  };

  const allScheduled = candidates.length > 0 && candidates.every((c) => {
    const s = schedules[c.id];
    return s?.date && s?.time;
  });

  const requestOtp = async () => {
    const email = (form.employer_email || "").trim();
    if (!email || !email.includes("@")) { toast.error(t("booking.otp_email_required", "Please enter a valid email first")); return; }
    if (!allScheduled) { toast.error(t("booking.pick_date_time", "Please pick date & time for every candidate")); return; }
    if (!form.employer_name || !form.employer_phone) { toast.error(t("booking.pick_name_phone", "Please enter your name and phone")); return; }
    setOtpRequesting(true);
    try {
      await api.post("/booking/otp/request", { email, name: form.employer_name });
      setOtpSent(true);
      setOtpVerifiedEmail(email.toLowerCase());
      toast.success(t("booking.otp_sent", "Verification code sent — check your email"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("booking.otp_failed", "Failed to send verification code"));
    } finally { setOtpRequesting(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!allScheduled) { toast.error(t("booking.pick_date_time", "Please pick date & time for every candidate")); return; }
    if (!form.employer_name || !form.employer_phone) { toast.error(t("booking.pick_name_phone", "Please enter your name and phone")); return; }
    // Ensure no two candidates share the same date+time slot in this submission
    const seen = new Set();
    for (const c of candidates) {
      const s = schedules[c.id];
      const key = `${s.date.toISOString().slice(0,10)}|${s.time}`;
      if (seen.has(key)) {
        toast.error(t("booking.time_conflict", "Two candidates share the same date & time. Please stagger their schedules."));
        return;
      }
      seen.add(key);
    }
    if (otpEnabled) {
      if (!form.employer_email) { toast.error(t("booking.otp_email_required", "Please enter a valid email first")); return; }
      if (!otpSent || !otpCode || otpCode.length < 6) { toast.error(t("booking.otp_code_required", "Please enter the 6-digit verification code")); return; }
    }
    // Show confirmation modal — actual submission happens on confirmClick().
    setShowConfirm(true);
  };

  const confirmAndSubmit = async () => {
    setShowConfirm(false);
    setSubmitting(true);
    try {
      const items = candidates.map((c) => ({
        candidate_id: c.id,
        date: schedules[c.id].date.toISOString().slice(0, 10),
        time: schedules[c.id].time,
      }));
      const body = { ...form, items };
      if (otpEnabled && otpCode) body.otp_code = otpCode.trim();
      if (employer?.id) body.employer_id = employer.id;
      await api.post("/bookings", body);
      toast.success(t("booking.success", "Interview booking submitted!"));
      // WhatsApp handoff — list every candidate on separate lines
      const num = settings?.whatsapp_number || "+6738123456";
      const lines = candidates.map((c, i) => {
        const s = schedules[c.id];
        const d = s.date.toISOString().slice(0, 10);
        return `${i + 1}. *${c.name}* — ${d} @ ${s.time}`;
      }).join("\n");
      const msg =
        `Hello Adelina, I would like to book ${candidates.length === 1 ? "an interview" : `${candidates.length} interviews`}.\n\n` +
        `${lines}\n\n` +
        `*Name:* ${form.employer_name}\n` +
        `*Phone:* ${form.employer_phone}\n` +
        `*Email:* ${form.employer_email || "-"}\n` +
        `*Company:* ${form.company || "-"}\n` +
        `*Notes:* ${form.notes || "-"}`;
      openWhatsApp(num, msg);
      setTimeout(() => navigate("/catalog"), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit booking");
    } finally {
      setSubmitting(false);
    }
  };

  if (!primaryCandidate) return <div className="max-w-7xl mx-auto px-6 py-20">{t("common.loading", "Loading…")}</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="booking-page">
      <div className="overline mb-4">{t("booking.overline", "Interview Booking")}</div>
      <h1 className="font-serif text-4xl md:text-5xl text-zinc-950 tracking-tight">
        {candidates.length > 1
          ? t("booking.title_multi", "Book {{n}} interviews", { n: candidates.length })
          : t("booking.title", "Book an interview with {{name}}", { name: primaryCandidate.name })}
      </h1>

      {/* -------- First-Come, First-Serve notice -------- */}
      <div className="mt-6 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 via-white to-amber-50/50 p-5 md:p-6 flex items-start gap-4" data-testid="fcfs-notice">
        <div className="shrink-0 w-11 h-11 rounded-full bg-[#b8860b] text-zinc-950 flex items-center justify-center font-serif text-xl">1</div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="text-[10.5px] tracking-[0.22em] uppercase text-[#b8860b] font-semibold">
              {t("booking.fcfs_overline", "Booking Policy")}
            </span>
            <span className="px-2 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-[10px] uppercase tracking-wider text-amber-900 font-semibold">
              {t("booking.fcfs_badge", "First Come, First Serve")}
            </span>
            <span className="px-2 py-0.5 rounded-full bg-zinc-100 border border-zinc-300 text-[10px] uppercase tracking-wider text-zinc-800 font-semibold">
              {t("booking.multi_badge", "Up to {{n}} candidates", { n: MAX_CANDIDATES })}
            </span>
          </div>
          <h3 className="font-serif text-xl md:text-2xl text-zinc-950 tracking-tight">
            {t("booking.multi_title", "Book up to {{n}} candidates in one go", { n: MAX_CANDIDATES })}
          </h3>
          <p className="mt-1.5 text-[13.5px] leading-relaxed text-zinc-700">
            {t("booking.multi_desc", "Each candidate gets their own date and time — schedules must not overlap. Whoever books first gets the slot.")}
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="mt-10 space-y-6">
        {/* Per-candidate schedule cards */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="overline">{t("booking.section_candidates", "Candidates & Schedules")}</div>
            <div className="text-xs text-zinc-500">{candidates.length} / {MAX_CANDIDATES}</div>
          </div>
          {candidates.map((c, i) => (
            <CandidateScheduleCard
              key={c.id}
              index={i}
              candidate={c}
              schedule={schedules[c.id]}
              onSchedule={setSchedule}
              onRemove={i === 0 ? null : () => removeExtra(c.id)}
              t={t}
            />
          ))}
          {candidates.length < MAX_CANDIDATES && !pickerOpen && (
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="w-full h-14 rounded-2xl border-2 border-dashed border-zinc-300 hover:border-[#b8860b] hover:bg-amber-50/30 text-sm text-zinc-700 hover:text-[#8b6508] font-medium inline-flex items-center justify-center gap-2 transition"
              data-testid="booking-add-candidate"
            >
              <Plus size={16} /> {t("booking.add_candidate", "Add another candidate")} ({candidates.length + 1} / {MAX_CANDIDATES})
            </button>
          )}
          {pickerOpen && (
            <CandidatePicker
              excludeIds={candidates.map((c) => c.id)}
              onPick={pickCandidate}
              onCancel={() => setPickerOpen(false)}
              t={t}
            />
          )}
        </div>

        {/* Employer details */}
        <div className="bg-white border border-zinc-200 rounded-3xl p-8">
          <div className="overline mb-3">{t("booking.step_details", "Your details")}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="ename">{t("booking.name", "Full Name")} *</Label>
              <Input id="ename" value={form.employer_name} onChange={(e) => setForm({ ...form, employer_name: e.target.value })} data-testid="input-name" required />
            </div>
            <div>
              <Label htmlFor="ephone">{t("booking.phone", "Phone / WhatsApp")} *</Label>
              <Input id="ephone" value={form.employer_phone} onChange={(e) => setForm({ ...form, employer_phone: e.target.value })} data-testid="input-phone" required />
            </div>
            <div>
              <Label htmlFor="eemail">{t("booking.email", "Email")} *</Label>
              <Input id="eemail" type="email" value={form.employer_email} onChange={(e) => changeEmail(e.target.value)} data-testid="input-email" required />
            </div>
            <div>
              <Label htmlFor="ecompany">{t("booking.company", "Company (optional)")}</Label>
              <Input id="ecompany" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="input-company" />
            </div>
          </div>
          <div className="mt-4">
            <Label htmlFor="enotes">{t("booking.notes", "Notes")}</Label>
            <Textarea id="enotes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="input-notes" />
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="etz">{t("booking.timezone", "Your timezone")}</Label>
              <select
                id="etz"
                value={form.employer_timezone}
                onChange={(e) => setForm({ ...form, employer_timezone: e.target.value })}
                className="w-full h-10 rounded-md border border-zinc-300 bg-white px-3 text-sm"
                data-testid="input-timezone"
              >
                {TIMEZONE_OPTIONS.map((tz) => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
              <div className="text-[11px] text-zinc-500 mt-1">
                {t("booking.timezone_hint", "Interview times are shown in Adelina HQ time (Asia/Brunei). We'll also send you a reminder in your local time.")}
              </div>
            </div>
          </div>
        </div>

        {/* OTP verification */}
        {otpEnabled && (
          <div className="rounded-2xl border border-zinc-200 bg-zinc-50/60 p-6">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck size={16} className="text-[#b8860b]" />
              <div className="overline">{t("booking.step_verify", "Verify your email")}</div>
            </div>
            <p className="text-sm text-zinc-700 mb-4 leading-relaxed">
              {t("booking.verify_desc", "We send a 6-digit code to your email to confirm this booking is real. This helps us protect our candidates from spam bookings.")}
            </p>
            {!otpSent ? (
              <button
                type="button"
                onClick={requestOtp}
                disabled={otpRequesting}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-zinc-950 text-sm font-medium hover:bg-zinc-950 hover:text-white transition-colors disabled:opacity-60"
                data-testid="request-otp-btn"
              >
                {otpRequesting ? <Loader2 size={14} className="animate-spin" /> : <MailCheck size={14} />}
                {otpRequesting ? t("booking.otp_sending", "Sending…") : t("booking.otp_send_btn", "Send verification code")}
              </button>
            ) : (
              <div>
                <div className="flex items-center gap-2 text-sm text-emerald-800 mb-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5">
                  <MailCheck size={14} className="text-emerald-700" />
                  <span>{t("booking.otp_sent_hint", "Code sent to {{email}}. Check your inbox (and spam) — it expires in 10 minutes.", { email: form.employer_email })}</span>
                </div>
                <Label htmlFor="otp-code" className="text-xs uppercase tracking-wider">{t("booking.otp_code_label", "6-digit code")}</Label>
                <div className="flex items-center gap-3 mt-1.5">
                  <Input
                    id="otp-code"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, ""))}
                    placeholder="123456"
                    className="w-40 h-11 text-xl tracking-[0.4em] font-mono text-center"
                    data-testid="otp-input"
                    required
                  />
                  <button
                    type="button"
                    onClick={requestOtp}
                    disabled={otpRequesting}
                    className="text-xs text-zinc-600 hover:text-[#b8860b] underline"
                    data-testid="resend-otp-btn"
                  >
                    {otpRequesting ? t("booking.otp_sending", "Sending…") : t("booking.otp_resend", "Resend code")}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !allScheduled || (otpEnabled && (!otpSent || otpCode.length < 6))}
          className="btn-gold w-full md:w-auto disabled:opacity-60 inline-flex items-center gap-2 justify-center"
          data-testid="submit-booking"
        >
          {submitting && <Loader2 size={14} className="animate-spin" />}
          {submitting
            ? t("booking.submitting", "Submitting…")
            : candidates.length > 1
              ? t("booking.submit_multi", "Submit {{n}} bookings & continue on WhatsApp", { n: candidates.length })
              : t("booking.submit", "Submit & Continue on WhatsApp")}
        </button>
      </form>

      {/* Confirmation modal — shown before the actual submit + WA handoff */}
      {showConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          data-testid="booking-confirm-modal"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-zinc-200 sticky top-0 bg-white rounded-t-2xl">
              <h2 className="text-lg font-semibold text-zinc-950">
                {t("booking.confirm_title", "Confirm Interview Booking")}
              </h2>
              <p className="text-xs text-zinc-500 mt-1">
                {t("booking.confirm_hint", "Review the summary below. After you confirm, we will continue to Adelina WhatsApp.")}
              </p>
            </div>
            <div className="px-6 py-5 space-y-4 text-sm text-zinc-800">
              <section>
                <div className="overline mb-2">{t("booking.confirm_candidates", "Candidates & Schedule")}</div>
                <ul className="space-y-1.5">
                  {candidates.map((c, i) => {
                    const s = schedules[c.id] || {};
                    const d = s.date ? s.date.toISOString().slice(0, 10) : "-";
                    return (
                      <li key={c.id} className="flex items-start gap-2" data-testid={`confirm-cand-${i}`}>
                        <span className="text-[#b8860b] font-semibold mt-0.5">{i + 1}.</span>
                        <div>
                          <div className="font-medium text-zinc-950">{c.name}</div>
                          <div className="text-xs text-zinc-600">{d} @ {s.time || "-"}</div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
              <section className="border-t border-zinc-200 pt-4">
                <div className="overline mb-2">{t("booking.confirm_employer", "Your Details")}</div>
                <div className="grid grid-cols-3 gap-x-3 gap-y-1 text-xs">
                  <div className="text-zinc-500">{t("booking.name", "Name")}</div>
                  <div className="col-span-2 text-zinc-900 font-medium">{form.employer_name || "-"}</div>
                  <div className="text-zinc-500">{t("booking.phone", "Telepon")}</div>
                  <div className="col-span-2 text-zinc-900">{form.employer_phone || "-"}</div>
                  <div className="text-zinc-500">{t("booking.email", "Email")}</div>
                  <div className="col-span-2 text-zinc-900">{form.employer_email || "-"}</div>
                  <div className="text-zinc-500">{t("booking.company", "Perusahaan")}</div>
                  <div className="col-span-2 text-zinc-900">{form.company || "-"}</div>
                  {form.notes && (<>
                    <div className="text-zinc-500">{t("booking.notes", "Notes")}</div>
                    <div className="col-span-2 text-zinc-900">{form.notes}</div>
                  </>)}
                </div>
              </section>
            </div>
            <div className="px-6 py-4 border-t border-zinc-200 flex gap-2 justify-end bg-white rounded-b-2xl sticky bottom-0">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 rounded-full border border-zinc-300 text-sm hover:bg-zinc-50"
                data-testid="booking-confirm-cancel"
              >
                {t("action.cancel", "Cancel")}
              </button>
              <button
                type="button"
                onClick={confirmAndSubmit}
                disabled={submitting}
                className="btn-gold text-sm inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="booking-confirm-submit"
              >
                {submitting && <Loader2 size={14} className="animate-spin" />}
                {t("booking.confirm_submit", "Confirm & Continue to WhatsApp")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
